import IosShareIcon from '@mui/icons-material/IosShare';
import ModeOfTravelIcon from '@mui/icons-material/ModeOfTravel';
import { ButtonGroup, Grid, useMediaQuery } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import RoutalStar from '../../../../assets/img/routal-star.svg';
import DialogWarning from '../../../../assets/svg/DialogWarning';
import OptimizerOptionIcon from '../../../../assets/svg/Plan/DetailHeader/OptimizerOptionIcon';
import ConfirmDialog from '../../../../dialogs/ConfirmDialog';
import { DispatchDialog } from '../../../../dialogs/DispatchDialog';
import { Stop } from '../../../../domain/stop/Stop';
import { useOptimizePlan } from '../../../../hooks/plan/useOptimizePlan';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import eventsPanel from '../../../../services/events';
import { anyStopHasAnyWarnings } from '../../shared/OptimizationWarningsTooltip';
import { OptimizerOptionsDialog } from './OptimizerOptionsDialog';

export const PlanHeaderActions = () => {
  const intl = useIntl();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: stops } = useRetrieveActivePlanStops();
  const { data: planGeoFences } = useRetrieveActivePlanGeoFences();
  const { isLoading: optimizingPlan, run: optimizePlan } = useOptimizePlan();
  const isLargeDesktop = useMediaQuery(`(min-width: 1920px)`);
  const isMediumDesktop = useMediaQuery(`(min-width: 1600px)`);
  const isHD = useMediaQuery(`(min-width: 1280px)`);
  const isTablet = useMediaQuery(`(min-width: 768px)`);

  const [keepCurrentAssignment, setKeepCurrentAssignment] = useState<boolean>(true);
  const [dialog, setDialog] = useState<`optimize` | `optimizerOptions` | `dispatch` | `warnings` | undefined>(
    undefined
  );

  const handleOptimizePlan = (keepCurrent?: boolean) => {
    if (activePlan) {
      optimizePlan({
        plan: activePlan,
        keepCurrentAssignment: keepCurrent,
      });
    }
  };

  const getOptimizeButtonTooltip = () => {
    if (
      (stops ?? []).length === 0 ||
      (drivers ?? []).length === 0 ||
      (drivers ?? []).filter((route: any) => route.is_locked).length === (drivers ?? []).length
    ) {
      return intl.formatMessage({ id: `planner.plan_details.header.optimize.disabled` });
    }

    if (optimizingPlan) {
      return intl.formatMessage({ id: `planner.plan_details.header.optimize.optimizing` });
    }

    return intl.formatMessage({ id: `planner.plan_details.header.optimize.tooltip` });
  };

  return (
    <>
      <ButtonGroup disableElevation disableRipple disableFocusRipple variant="contained">
        <RoutalTooltip
          id="optimize-button"
          title={
            <Text intlId={getOptimizeButtonTooltip()} variant="body" style={{ cursor: `pointer`, color: `white` }} />
          }
          placement="bottom"
        >
          <Button
            style={{
              marginRight: `1px`,
              borderBottomRightRadius: isTablet && !isHD ? undefined : 0,
              borderTopRightRadius: isTablet && !isHD ? undefined : 0,
            }}
            onClick={() => {
              if (
                (stops ?? []).length === 0 ||
                (drivers ?? []).length === 0 ||
                optimizingPlan ||
                (drivers ?? []).filter((route: any) => route.is_locked).length === (drivers ?? []).length
              ) {
                return;
              }

              const requiresConfirmation = (stops ?? []).some((stop: Stop) => stop.driver_id);
              if (!requiresConfirmation) {
                if (
                  anyStopHasAnyWarnings({
                    stops: stops ?? [],
                    drivers: drivers ?? [],
                    planGeoFences: planGeoFences ?? [],
                  })
                ) {
                  setDialog(`warnings`);
                } else {
                  handleOptimizePlan();
                }
                return;
              }
              setKeepCurrentAssignment(true);
              setDialog(`optimize`);
            }}
          >
            {isHD || isMediumDesktop || isLargeDesktop ? (
              <div style={{ margin: `0 16px` }}>
                {optimizingPlan ? (
                  <>
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      style={{
                        width: `114px`,
                        height: `16px`,
                        display: `flex`,
                        flexDirection: `row`,
                        alignItems: `center`,
                      }}
                    >
                      <div className="lds-ellipsis-little" style={{ height: `8px` }}>
                        <div style={{ top: `2px`, backgroundColor: `white` }} />
                        <div style={{ top: `2px`, backgroundColor: `white` }} />
                        <div style={{ top: `2px`, backgroundColor: `white` }} />
                      </div>
                    </Grid>
                    <HSpacer small />
                  </>
                ) : (
                  <Grid container direction="row" wrap="nowrap">
                    <Text
                      intlId="planner.plan_details.header.optimize.button"
                      style={{ color: `white`, cursor: `pointer` }}
                    />
                    <HSpacer small />
                    <img src={RoutalStar} alt="" draggable={false} style={{ cursor: `pointer` }} />
                  </Grid>
                )}
              </div>
            ) : (
              <ModeOfTravelIcon />
            )}
          </Button>
        </RoutalTooltip>
        {(isLargeDesktop || isMediumDesktop || isHD) && (
          <Button
            style={{
              padding: `4px 8px`,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
            }}
            onClick={() => setDialog(`optimizerOptions`)}
          >
            <OptimizerOptionIcon />
          </Button>
        )}
      </ButtonGroup>

      {(isLargeDesktop || isMediumDesktop) && <HSpacer medium />}
      {(isHD || isTablet) && <HSpacer small />}
      <RoutalTooltip
        id="Share"
        title={<Text intlId="dispatch_dialog.title" variant="body" style={{ cursor: `pointer`, color: `white` }} />}
        placement="bottom"
      >
        <Button
          id="dispatch"
          styleType="secondary"
          disabled={(drivers ?? []).length === 0}
          onClick={() => {
            setDialog(`dispatch`);
            eventsPanel.planDispatch();
          }}
        >
          {isMediumDesktop || isLargeDesktop ? (
            <Text intlId="planner.plan_details.header.dispatch.button" style={{ color: `white`, cursor: `pointer` }} />
          ) : (
            <IosShareIcon />
          )}
        </Button>
      </RoutalTooltip>

      <OptimizerOptionsDialog isOpen={Boolean(dialog === `optimizerOptions`)} onClose={() => setDialog(undefined)} />

      <RoutalDialog
        open={Boolean(dialog === `optimize`)}
        titleContent={<Text variant="h5" intlId="planner.plan_details.header.confirm_optimize_dialog.title" />}
        cancelButtonText={intl.formatMessage({ id: `cancel` })}
        confirmButtonText={intl.formatMessage({ id: `confirm` })}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          if (
            anyStopHasAnyWarnings({ stops: stops ?? [], drivers: drivers ?? [], planGeoFences: planGeoFences ?? [] })
          ) {
            setDialog(`warnings`);
          } else {
            handleOptimizePlan(keepCurrentAssignment);
            setDialog(undefined);
          }
        }}
      >
        <Grid container direction="column" wrap="nowrap">
          <Grid
            item
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
            style={{
              padding: `16px`,
              borderRadius: `8px`,
              backgroundColor: `${RoutalPalette.warning}4D`,
            }}
          >
            <Checkbox
              checked={!keepCurrentAssignment}
              onChange={(e: any, value: any) => {
                setKeepCurrentAssignment(!value);
              }}
            />
            <Grid item container direction="row" wrap="nowrap" alignItems="center">
              <HSpacer small />
              <Text tagName="span" intlId="planner.plan_details.header.confirm_optimize_dialog.checkbox" />
            </Grid>

            <Grid
              item
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              style={{
                width: `unset`,
              }}
            >
              <DialogWarning style={{ color: RoutalPalette.primary40 }} />
            </Grid>
          </Grid>
        </Grid>
      </RoutalDialog>

      <ConfirmDialog
        title={<Text variant="h5" intlId="planner.plan_details.header.optimization_warnings_dialog.title" />}
        description={<Text variant="body" intlId="planner.plan_details.header.optimization_warnings_dialog.content" />}
        open={Boolean(dialog === `warnings`)}
        onCloseDialog={() => setDialog(undefined)}
        onConfirm={() => {
          handleOptimizePlan(keepCurrentAssignment);
          setDialog(undefined);
        }}
      />

      <DispatchDialog handleCloseDialog={setDialog} dispatchDialogOpen={Boolean(dialog === `dispatch`)} />
    </>
  );
};
